import { get, post, uploadApiFile } from '../client'

export type MarketingTargetMetric =
  | 'net_sales'
  | 'valid_orders'
  | 'first_orders'
  | 'repeat_orders'
  | 'product_quantity'
  | 'conversion_rate'
export type MarketingTargetStatus =
  | 'draft'
  | 'pending_approval'
  | 'pending_start'
  | 'active'
  | 'cooling'
  | 'pending_settlement'
  | 'completed'
  | 'cancelled'
export interface MarketingTargetDay {
  date: string
  baseValue: number
  sprintValue: number
}
export interface MarketingTargetObject {
  objectId: number
  name: string
  baseValue: number
  sprintValue: number
  days: MarketingTargetDay[]
}
export interface MarketingTargetMember {
  memberId: number
  name: string
  departmentId: number
  departmentName: string
  baseValue: number
  sprintValue: number
  days: MarketingTargetDay[]
}
export interface MarketingTargetGroup {
  groupId: number
  name: string
  baseValue: number
  sprintValue: number
  days: MarketingTargetDay[]
  members: MarketingTargetMember[]
}
export interface MarketingTargetChannel {
  channel: 'system' | 'wecom' | 'dingtalk' | 'feishu'
  enabled: boolean
  webhook: string
  secret: string
}
export interface MarketingTargetCelebration {
  enabled: boolean
  title: string
  content: string
  imageId: number
}
export interface MarketingTargetConfig {
  title: string
  /** 目标项目范围；0 表示未限定项目并统计当前公司全部项目。 */
  projectId: number
  metric: MarketingTargetMetric
  objectType: 'employee' | 'department'
  /** 上海时间 YYYY-MM-DD HH:mm；兼容旧版 YYYY-MM-DD 的当日零点。 */
  startDate: string
  /** 不包含的结束分钟；旧版 YYYY-MM-DD 仍包含当日，按次日零点结束。 */
  endDate: string
  ownerId: number
  baseValue: number
  sprintValue: number
  minimumSample: number
  /** 页面内部保留的扁平编辑对象；请求前转换为 groups。 */
  coolingPeriodDays?: number
  objects: MarketingTargetObject[]
  groups?: MarketingTargetGroup[]
  notification: {
    behind: boolean
    stageMissed: boolean
    frequency: 'daily_18' | 'monday_10'
    channels: MarketingTargetChannel[]
  }
  baseCelebration: MarketingTargetCelebration
  sprintCelebration: MarketingTargetCelebration
  rewards: { baseReward: string; sprintReward: string; basePenalty: string }
}
export interface MarketingTargetRecord {
  id: number
  code: string
  title: string
  /** 目标项目范围；0 表示未限定项目并统计当前公司全部项目。 */
  projectId: number
  metric: MarketingTargetMetric
  objectType: string
  ownerId: number
  status: MarketingTargetStatus
  revision: number
  version: number
  approvalInstanceId: number
  startAt: string | null
  endAt: string | null
  cutoffAt: string | null
  createdAt: string
  updatedAt: string
}
export interface MarketingTargetListItem extends MarketingTargetRecord {
  baseValue: number | null
  sprintValue: number | null
  ownerName: string
  overallVisible: boolean
  canManage: boolean
  /** 服务端按当前目标、结算权限与数据范围计算的单行结算能力。 */
  canSettle: boolean
}
export type MarketingTargetServerConfig = Omit<MarketingTargetConfig, 'objects' | 'groups'> & {
  /** 服务端唯一接收与返回的组/成员配置。 */
  groups: MarketingTargetGroup[]
}
export interface MarketingTargetDetail {
  planUnavailableReason?: string
  target: MarketingTargetRecord
  config: MarketingTargetServerConfig
  canManage: boolean
  /** 服务端独立核验的后续任务调整权限，不授予取消或结算权限。 */
  canAdjustPlan?: boolean
  canSettle: boolean
  canEndCooling: boolean
  overallVisible: boolean
  progressAvailable: boolean
  progressMessage: string
}
export interface MarketingTargetOptions {
  projects: { id: number; name: string }[]
  departments: { id: number; name: string }[]
  employees: { userId: number; realName: string; departmentId: number }[]
  groups?: { id: number; name: string; members: { userId: number; realName: string }[] }[]
}
export interface MarketingTargetListQuery {
  page: number
  pageSize: number
  keyword?: string
  status?: string
}

/** 分页读取真实目标；params 为服务端筛选与分页条件。 */
export function getMarketingTargets(params: MarketingTargetListQuery) {
  return get<{
    list: MarketingTargetListItem[]
    total: number
    counts: Record<string, number>
    canManage: boolean
  }>('/business/marketingTarget/getList', { params: { ...params } })
}
/** 读取详情或授权编辑原值；id 是目标ID，edit 只用于管理页。 */
export function getMarketingTarget(id: number, edit = false) {
  return get<MarketingTargetDetail>(
    `/business/marketingTarget/${edit ? 'getConfig' : 'getDetail'}`,
    { params: { id } },
  )
}
/** 加载权限范围内真实项目、部门与员工；无请求参数。 */
export function getMarketingTargetOptions() {
  return get<MarketingTargetOptions>('/business/marketingTarget/getOptions')
}
/** 保存草稿；data 内 revision 为乐观锁，createKey 用于首次创建重试。 */
export interface MarketingTargetSaveResult {
  target: MarketingTargetRecord
  config: MarketingTargetServerConfig
  revision: number
}
/** 保存草稿；data 内 revision 为乐观锁，createKey 用于首次创建重试。 */
export function saveMarketingTarget(data: {
  id: number
  revision: number
  createKey: string
  config: MarketingTargetServerConfig
}) {
  return post<MarketingTargetSaveResult>('/business/marketingTarget/saveDraft', data)
}
/** 提交完整草稿配置；服务端会重新校准组、成员和日计划。 */
export function submitMarketingTarget(
  id: number,
  revision: number,
  config: MarketingTargetServerConfig,
) {
  return post('/business/marketingTarget/submitApproval', { id, revision, config })
}
/** 取消未生效目标；id/revision 用于并发校验。 */
export function cancelMarketingTarget(id: number, revision: number) {
  return post('/business/marketingTarget/cancel', { id, revision })
}
/** 明确均分双日计划；仅发送计划字段，不传通知凭据。 */
export function distributeMarketingTarget(config: MarketingTargetConfig) {
  return post<MarketingTargetConfig>('/business/marketingTarget/distribute', {
    startDate: config.startDate,
    endDate: config.endDate,
    metric: config.metric,
    baseValue: config.baseValue,
    sprintValue: config.sprintValue,
    objects: config.objects,
  })
}

/** 服务端标识真实统计的可用状态；页面只能展示该语义，不得通过零值猜测。 */
export type MarketingTargetDataStatus = 'complete' | 'pending' | 'incomplete'

/** 后端以最小单位返回双门槛结果与进度展示字段；正式结果、计划差额和完整性均由服务端计算。 */
export interface MarketingTargetMeasure {
  departmentId?: number
  objectId: number
  name: string
  /** 责任人所属组织；部门目标直接返回目标部门名称。 */
  organizationName: string
  period: string
  baseValue: number
  sprintValue: number
  actual: number
  /** 截至北京时间统计日的累计基础计划；不可安全累计的指标不返回。 */
  plannedToDate?: number
  plannedToDateAvailable: boolean
  /** 实际相对截至统计日计划的差额；正数领先、负数落后。 */
  relativeToPlan?: number
  baseAchievementPercent: number
  sprintAchievementPercent: number
  baseRemainingGap: number
  sprintRemainingGap: number
  dataStatus: MarketingTargetDataStatus
  numerator: number
  denominatorIncomplete?: boolean
  sample: number
  baseResult: 'achieved' | 'not_achieved' | 'insufficient_sample'
  sprintResult: 'achieved' | 'not_achieved' | 'insufficient_sample'
  complete: boolean
  reasons: string[]
}

/** 整体权限可见时返回的目标进度概览；不具备整体权限时服务端不会返回该字段。 */
export interface MarketingTargetProgressSummary {
  asOfDate: string
  timeProgressPercent: number
  actual: number
  plannedToDate?: number
  plannedToDateAvailable: boolean
  relativeToPlan?: number
  baseValue: number
  sprintValue: number
  baseAchievementPercent: number
  sprintAchievementPercent: number
  baseRemainingGap: number
  sprintRemainingGap: number
  baseResult: MarketingTargetMeasure['baseResult']
  sprintResult: MarketingTargetMeasure['sprintResult']
  dataStatus: MarketingTargetDataStatus
}

/** 创建时成员快照的独立考核结果；成员没有单独奖惩配置。 */
export interface MarketingTargetResultMember extends MarketingTargetMeasure {
  memberId: number
  groupId: number
}
/** 创建时参与组及其成员考核结果树；冻结后来自不可变结果快照。 */
export interface MarketingTargetResultGroup extends MarketingTargetMeasure {
  /** 1 为首审部门口径；缺失或 0 保留历史口径。 */
  attributionVersion?: number
  /** 完整授权时返回原名单外已确认贡献；单位与当前指标一致。 */
  unlistedActual?: number
  groupId: number
  rewards: MarketingTargetConfig['rewards']
  members: MarketingTargetResultMember[]
}
export interface MarketingTargetProgress {
  planUnavailableReason?: string
  targetId: number
  version: number
  asOf: string
  frozen: boolean
  /** 全局进度可见时的状态化概览，未授权或旧快照可能为空。 */
  summary?: MarketingTargetProgressSummary
  overall: MarketingTargetMeasure | null
  objects: MarketingTargetMeasure[]
  days: MarketingTargetMeasure[]
  weeks: MarketingTargetMeasure[]
  months: MarketingTargetMeasure[]
  /** 已正式创建目标的组—人员考核结果树；会随冻结快照一并固化。 */
  resultGroups: MarketingTargetResultGroup[]
}
export interface MarketingTargetSource {
  sourceType: 'order' | 'fan'
  orderId?: number
  orderNo?: string
  customerId?: number
  ownerId: number
  ownerName: string
  occurredAt: string
  amountCents: number
  refundCents: number
  quantity: number
  returnedQuantity: number
  contribution: number
  valid: boolean
  evidenceStatus: 'complete' | 'incomplete'
}
export interface MarketingTargetSourcePage {
  list: MarketingTargetSource[]
  total: number
  page: number
  pageSize: number
  asOf: string
}
export interface MarketingTargetSourceQuery {
  id: number
  scopeType: 'group' | 'member'
  scopeId: number
  page: number
  pageSize: number
  keyword?: string
}
/** 独立读取真实进度或不可变结果；objectId只能选择当前授权对象的日周月明细。 */
export function getMarketingTargetProgress(id: number, objectId?: number) {
  return get<MarketingTargetProgress>('/business/marketingTarget/getProgress', {
    params: { id, objectId },
  })
}
/** 读取服务端已校验的组或人员考核来源；关键词与分页均由服务端处理。 */
export function getMarketingTargetSources(params: MarketingTargetSourceQuery) {
  return get<MarketingTargetSourcePage>('/business/marketingTarget/getSources', {
    params: { ...params },
  })
}
/**
 * 提前结束冷静期并进入待结算；服务端独立校验权限、范围及 revision。
 * @param id 目标ID。
 * @param revision 详情返回的当前修订号，用于乐观锁。
 */
export function earlyEndMarketingTarget(id: number, revision: number) {
  return post('/business/marketingTarget/earlyEndCooling', { id, revision })
}
/**
 * 人工结算待结算目标；服务端独立校验结算权限、范围及 revision。
 * @param id 目标ID。
 * @param revision 详情返回的当前修订号，用于乐观锁。
 */
export function settleMarketingTarget(id: number, revision: number) {
  return post('/business/marketingTarget/settle', { id, revision })
}

export interface TargetCelebrationItem {
  objectId: number
  name: string
  level: 'base' | 'sprint'
  title: string
  content: string
  imageId: number
  /** 以下字段由冻结快照提供；历史喜报缺失时按旧 title/name/content 安全降级。 */
  subjectType?: 'employee' | 'department' | 'overall'
  subjectName?: string
  targetName?: string
  stageTitle?: string
  metricKey?: MarketingTargetMetric
  metricLabel?: string
  displayValue?: string
  actualValue?: number
}
export interface TargetCelebrationClaim {
  id: number
  targetId: number
  version: number
  claimToken: string
  expiresAt: string
  items: TargetCelebrationItem[]
}
export interface TargetAuditRecord {
  planChanges?: MarketingTargetPlanChange[]
  id: number
  version: number
  revision: number
  actorId: number
  actorName?: string
  action: string
  summary: string
  createdAt: string
}
/** 分页读取授权管理范围的配置与操作记录；id为目标，page/pageSize为分页，不返回配置原值。 */
export function getTargetAuditLogs(id: number, page: number, pageSize: number) {
  return get<{ list: TargetAuditRecord[]; total: number }>(
    '/business/marketingTarget/getAuditLogs',
    { params: { id, page, pageSize } },
  )
}
export interface TargetDeliveryRecord {
  deliveryScope: 'self' | 'authorized_recipient' | 'overall_summary'
  id: number
  eventType: string
  channel: string
  status: string
  attempts: number
  dueAt: string
  sentAt: string | null
  errorCode: string
}
/** 领取本账号一份合并喜报；服务端同时核验权限、免打扰、固定TTL与多端租约。 */
export function claimTargetCelebration() {
  return post<TargetCelebrationClaim | null>('/business/marketingTarget/claimCelebration', {})
}
/** 确认已关闭喜报；id为领取记录，claimToken为本次租约令牌，仅用于当前内存请求。 */
export function ackTargetCelebration(id: number, claimToken: string) {
  return post('/business/marketingTarget/ackCelebration', { id, claimToken })
}
/** 分页读取脱敏投递日志；id为目标，page/pageSize为分页，不返回发送正文或凭据。 */
export function getTargetDeliveryLogs(id: number, page: number, pageSize: number) {
  return get<{ list: TargetDeliveryRecord[]; total: number }>(
    '/business/marketingTarget/getDeliveryLogs',
    { params: { id, page, pageSize } },
  )
}
/** 上传真实喜报图片；file走统一客户端上传链路，后端校验公司归属与内容。 */
export function uploadTargetImage(file: File) {
  return uploadApiFile<{ id: number; url: string }>('/business/marketingTarget/uploadImage', file)
}
/** 获取已授权资源；id为资源，targetId=0只允许管理配置权限，否则核验目标引用。 */
export function getTargetImage(id: number, targetId = 0) {
  return get<{ id: number; url: string }>('/business/marketingTarget/getImage', {
    params: { id, targetId },
  })
}

export interface MarketingTargetPlanBucket {
  key: string
  weekStart?: string
  weekEnd?: string
  month?: string
  baseValue: number
  sprintValue: number
}
export interface MarketingTargetPlanMember {
  objectId: number
  name: string
  baseValue: number
  sprintValue: number
  days: MarketingTargetDay[]
  weeks: MarketingTargetPlanBucket[]
  months: MarketingTargetPlanBucket[]
  historyDays?: MarketingTargetDay[]
  lastAdjustedAt?: string
  lastActorName?: string
}
export interface MarketingTargetFuturePlan {
  adjustmentModes?: string[]
  targetId: number
  version: number
  planRevision: number
  today: string
  canAdjust: boolean
  groups: Array<{
    departmentId: number
    departmentName: string
    days: MarketingTargetDay[]
    weeks: MarketingTargetPlanBucket[]
    months: MarketingTargetPlanBucket[]
    members: MarketingTargetPlanMember[]
  }>
}
export interface MarketingTargetLegacyPlanAdjustInput {
  adjustmentMode?: never
  edits?: never
  reason?: never
  expectedVersion?: never
  expectedPlanRevision: number
  objectId: number
  scope: 'day' | 'week'
  date?: string
  weekStart?: string
  baseValue: number
  sprintValue: number
}
/** 本人未来金额计划的固定行；值均为整数分，日和周字段互斥。 */
export type MarketingTargetPlanEdit = (
  { date: string; weekStart?: never } | { weekStart: string; date?: never }
) & {
  baseValue: number
  sprintValue: number
}
export interface MarketingTargetMemberPlanAdjustInput {
  adjustmentMode: 'member_future'
  expectedVersion: number
  expectedPlanRevision: number
  objectId: number
  scope: 'day' | 'week'
  edits: MarketingTargetPlanEdit[]
  reason?: string
  date?: never
  weekStart?: never
  baseValue?: never
  sprintValue?: never
}
export type MarketingTargetPlanAdjustInput =
  MarketingTargetLegacyPlanAdjustInput | MarketingTargetMemberPlanAdjustInput
/** 正式保存的新模式必须携带批次原因，预览仍使用不要求原因的输入类型。 */
export type MarketingTargetPlanSubmitInput =
  MarketingTargetLegacyPlanAdjustInput | (MarketingTargetMemberPlanAdjustInput & { reason: string })
export interface MarketingTargetAffectedDay extends MarketingTargetDay {
  departmentId: number
  objectId: number
  beforeBaseValue: number
  beforeSprintValue: number
}
export interface MarketingTargetPlanChange {
  objectId: number
  departmentId: number
  date: string
  planRevision: number
  beforeBaseValue: number
  beforeSprintValue: number
  afterBaseValue: number
  afterSprintValue: number
}
export interface MarketingTargetPlanAdjustResult {
  targetId: number
  version: number
  planRevision: number
  affectedDays: MarketingTargetAffectedDay[]
}
export interface MarketingTargetConfigScope {
  kind: 'department' | 'member'
  departmentId: number
  objectId?: number
  name: string
}
/** 读取目标id授权范围内的未来有效计划，不读取全量员工。 */
export function getMarketingTargetFuturePlan(id: number) {
  return get<MarketingTargetFuturePlan>(`/business/marketingTarget/${id}/future-plan`)
}
/** 请求服务端计算重分配预览；id为目标，data为用户输入，不落库。 */
export function previewMarketingTargetPlan(id: number, data: MarketingTargetPlanAdjustInput) {
  return post<MarketingTargetPlanAdjustResult>(
    `/business/marketingTarget/${id}/future-plan/preview`,
    data,
    data.adjustmentMode === 'member_future' ? { retry: false } : undefined,
  )
}
/** 提交目标id的后续计划调整；data携带期望修订号，不发送通知。 */
export function adjustMarketingTargetPlan(id: number, data: MarketingTargetPlanSubmitInput) {
  return post<MarketingTargetPlanAdjustResult>(
    `/business/marketingTarget/${id}/future-plan/adjust`,
    data,
    data.adjustmentMode === 'member_future' ? { retry: false } : undefined,
  )
}
